import { regionConfig } from '@/config/region'
import type { ExtractedIncidentMeta } from './types'
import { findRmcFix } from './nmea'
import { readBoxText, readTopLevelBoxes, type Mp4Box } from './mp4-boxes'

// Boxes dashcams are known to put ASCII NMEA in (BlackVue `gps `, Novatek `free`/`skip`, vendor `uuid`/`udta`).
const NMEA_BOXES = new Set(['gps ', 'free', 'skip', 'udta', 'uuid', 'moov'])
const NMEA_BOX_MAX_BYTES = 4 * 1024 * 1024
const MDAT_HEAD_BYTES = 2 * 1024 * 1024
const MOOV_MAX_BYTES = 8 * 1024 * 1024
const MAX_AGE_YEARS = 10
const REGION_MARGIN_DEGREES = 1

// ISO 6709 decimal-degrees form as written by phones: +27.4698+153.0251(+012.3)/
const ISO_6709 = /([+-]\d{2}\.\d+)([+-]\d{3}\.\d+)(?:[+-]\d+(?:\.\d+)?)?\//

/**
 * Read GPS and time that the dashcam wrote into the first video: NMEA RMC text in the usual boxes (and the start of
 * `mdat`), else an ISO 6709 location tag (location only). Reads File.slice() ranges only; nothing is uploaded.
 * Values that are implausible (outside the region, in the future, older than ten years) are dropped.
 * Returns null when nothing usable is found. Never throws on malformed files.
 */
export async function extractEmbeddedGps(
  files: File[],
  now: Date = new Date()
): Promise<ExtractedIncidentMeta | null> {
  const video = files.find((f) => f.type.startsWith('video/') || /\.mp4$/i.test(f.name))
  if (!video) return null

  const boxes = await readTopLevelBoxes(video)
  const meta = (await readRmc(video, boxes, now)) ?? (await readIsoLocation(video, boxes))
  if (!meta) return null

  const result: ExtractedIncidentMeta = {}
  if (meta.lat !== undefined && meta.lon !== undefined && isInRegion(meta.lat, meta.lon)) {
    result.lat = meta.lat
    result.lon = meta.lon
  }
  if (meta.dateTime && isPlausibleTime(meta.dateTime, now)) result.dateTime = meta.dateTime

  return result.lat === undefined && result.dateTime === undefined ? null : result
}

async function readRmc(
  file: File,
  boxes: Mp4Box[],
  now: Date
): Promise<ExtractedIncidentMeta | null> {
  // A fix with both a plausible place and time wins; a half-good one is only a fallback.
  let halfGood: ExtractedIncidentMeta | null = null
  for (const box of boxes) {
    const isMdat = box.type === 'mdat'
    if (!isMdat && !NMEA_BOXES.has(box.type)) continue
    if (!isMdat && box.size > NMEA_BOX_MAX_BYTES) continue

    const text = await readBoxText(file, box, isMdat ? MDAT_HEAD_BYTES : NMEA_BOX_MAX_BYTES)
    const fix = findRmcFix(text)
    if (!fix) continue
    const placeOk = isInRegion(fix.lat, fix.lon)
    const timeOk = isPlausibleTime(fix.dateTime, now)
    if (!placeOk && !timeOk) continue
    const meta = { lat: fix.lat, lon: fix.lon, dateTime: fix.dateTime }
    if (placeOk && timeOk) return meta
    halfGood ??= meta
  }
  return halfGood
}

async function readIsoLocation(file: File, boxes: Mp4Box[]): Promise<ExtractedIncidentMeta | null> {
  const moov = boxes.find((b) => b.type === 'moov')
  if (!moov || moov.size > MOOV_MAX_BYTES) return null

  const match = (await readBoxText(file, moov, MOOV_MAX_BYTES)).match(ISO_6709)
  if (!match) return null
  return { lat: parseFloat(match[1]), lon: parseFloat(match[2]) }
}

function isInRegion(lat: number, lon: number): boolean {
  const { sw, ne } = regionConfig.mapBounds
  return (
    lat >= sw.lat - REGION_MARGIN_DEGREES &&
    lat <= ne.lat + REGION_MARGIN_DEGREES &&
    lon >= sw.lng - REGION_MARGIN_DEGREES &&
    lon <= ne.lng + REGION_MARGIN_DEGREES
  )
}

function isPlausibleTime(dateTime: Date, now: Date): boolean {
  const oldest = new Date(now)
  oldest.setFullYear(oldest.getFullYear() - MAX_AGE_YEARS)
  return dateTime.getTime() <= now.getTime() && dateTime.getTime() >= oldest.getTime()
}
